/**
 * The ten-minute hold between Send to Client and the report email
 * (JIM-1003-05, Jim 2026-10-03). The server owns the clock: it stores when
 * the report goes (`sends_at`) and sends it then, whether or not this phone
 * is still open. The app only counts down to that time and asks how it went.
 */
import { useEffect, useState } from 'react';

export type SendReceipt = {
  sent_count: number;
  failed_count?: number;
  recipients: Array<{ email: string; name: string; status: string }>;
  no_contacts: boolean;
  client_signature?: {
    status: string;
    signer_name: string;
    signer_email: string;
    auto_applied?: boolean;
  } | null;
};

export type SendStatus = {
  state: 'idle' | 'queued' | 'sending' | 'sent' | 'failed';
  sends_at: string | null;
  seconds_left: number;
  error: string | null;
  result: SendReceipt | null;
};

/** How often to ask the server how a send went, once its time has come. */
export const SEND_POLL_MS = 4000;

/** Whole seconds until `sendsAt`, never below zero. */
export function secondsUntil(sendsAt: string, nowMs: number): number {
  const ms = new Date(sendsAt).getTime() - nowMs;
  return Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / 1000)) : 0;
}

/** 581 → "9:41"; 59 → "0:59". */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The clock time the report goes, e.g. "3:42 PM". */
export function sendsAtLabel(sendsAt: string): string {
  return new Date(sendsAt).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** What the inspector reads once a held report has gone. */
export function sentMessage(receipt: SendReceipt): string {
  if (receipt.no_contacts)
    return 'Report finalized, but no client contacts are set up to receive reports for this site. Nothing was emailed.';
  const n = receipt.sent_count;
  return `Report sent to the client (${n} recipient${n === 1 ? '' : 's'})`;
}

/** Seconds left on a hold, ticking once a second and stopping at zero. */
export function useCountdown(sendsAt: string | null): number {
  const [left, setLeft] = useState(() =>
    sendsAt ? secondsUntil(sendsAt, Date.now()) : 0
  );
  useEffect(() => {
    if (!sendsAt) {
      setLeft(0);
      return;
    }
    const tick = () => {
      const next = secondsUntil(sendsAt, Date.now());
      setLeft(next);
      return next;
    };
    if (tick() === 0) return;
    const timer = setInterval(() => {
      if (tick() === 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [sendsAt]);
  return left;
}
